import { useState } from "react";
import { Button, Menu, MenuItem } from "@mui/material";
import { ChevronDown } from "lucide-react";
import {
  RematchSession,
  RematchSessionStatus,
} from "protogen/advisors_service_pb";
import { useUpdateRematchSession } from "services/admin";

const CHANGEABLE_STATUSES = [
  RematchSessionStatus.REMATCH_ACTIVE_DRAFT,
  RematchSessionStatus.REMATCH_CLIENT_EMAIL_SENT,
  RematchSessionStatus.REMATCH_CLIENT_FOLLOWUP_SENT,
  RematchSessionStatus.REMATCH_ADVISOR_SELECTED,
  RematchSessionStatus.REMATCH_CANCELED,
  RematchSessionStatus.REMATCH_COMPLETE,
  RematchSessionStatus.REMATCH_DELETED,
] as const;

const STATUS_LABELS: Record<number, string> = {
  [RematchSessionStatus.REMATCH_ACTIVE]: "Active",
  [RematchSessionStatus.REMATCH_ACTIVE_DRAFT]: "Draft",
  [RematchSessionStatus.REMATCH_CLIENT_EMAIL_SENT]: "Client email sent",
  [RematchSessionStatus.REMATCH_CLIENT_FOLLOWUP_SENT]: "Client followup sent",
  [RematchSessionStatus.REMATCH_ADVISOR_SELECTED]: "Advisor selected",
  [RematchSessionStatus.REMATCH_CANCELED]: "Canceled",
  [RematchSessionStatus.REMATCH_COMPLETE]: "Complete",
  [RematchSessionStatus.REMATCH_DELETED]: "Deleted",
};

const STATUS_BY_NAME: Record<string, RematchSessionStatus> = {
  active: RematchSessionStatus.REMATCH_ACTIVE,
  active_draft: RematchSessionStatus.REMATCH_ACTIVE_DRAFT,
  client_email_sent: RematchSessionStatus.REMATCH_CLIENT_EMAIL_SENT,
  client_followup_sent: RematchSessionStatus.REMATCH_CLIENT_FOLLOWUP_SENT,
  advisor_selected: RematchSessionStatus.REMATCH_ADVISOR_SELECTED,
  canceled: RematchSessionStatus.REMATCH_CANCELED,
  complete: RematchSessionStatus.REMATCH_COMPLETE,
  deleted: RematchSessionStatus.REMATCH_DELETED,
};

interface Props {
  session: RematchSession;
  onUpdated?: () => void;
}

export default ({ session, onUpdated }: Props) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const { request: updateRematchSession, loading } = useUpdateRematchSession(
    () => onUpdated?.(),
  );

  const currentStatus = STATUS_BY_NAME[session.status];
  const availableStatuses = CHANGEABLE_STATUSES.filter(
    (status) => status !== currentStatus,
  );

  const handleStatusChange = async (status: RematchSessionStatus) => {
    setAnchor(null);
    await updateRematchSession({
      rematchSessionRef: session.ref,
      status,
    });
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        disabled={loading}
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        endIcon={<ChevronDown size={16} />}
        sx={{ whiteSpace: "nowrap" }}
      >
        Change Status
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
      >
        {availableStatuses.map((status) => (
          <MenuItem key={status} onClick={() => handleStatusChange(status)}>
            {STATUS_LABELS[status]}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
